import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const AccountSettings = ({ user, onUpdate }) => {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [emailForm, setEmailForm] = useState({
    newEmail: user?.email || '',
    password: ''
  });

  const [errors, setErrors] = useState({});

  const preferences = [
    {
      key: 'emailNotifications',
      title: 'E-Mail-Benachrichtigungen',
      description: 'Erhalten Sie Updates zu Ihren AI-Agents per E-Mail',
      enabled: true
    },
    {
      key: 'marketingEmails',
      title: 'Marketing-E-Mails',
      description: 'Informationen über neue Features und Angebote',
      enabled: false
    },
    {
      key: 'dataCollection',
      title: 'Anonyme Nutzungsstatistiken',
      description: 'Helfen Sie uns, das Produkt zu verbessern',
      enabled: true
    },
    {
      key: 'autoSave',
      title: 'Automatisches Speichern',
      description: 'Änderungen automatisch speichern',
      enabled: true
    }
  ];

  const [userPreferences, setUserPreferences] = useState(
    preferences?.reduce((acc, pref) => {
      acc[pref?.key] = pref?.enabled;
      return acc;
    }, {})
  );

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm?.currentPassword) {
      newErrors.currentPassword = 'Aktuelles Passwort ist erforderlich';
    }

    if (!passwordForm?.newPassword) {
      newErrors.newPassword = 'Neues Passwort ist erforderlich';
    } else if (passwordForm?.newPassword?.length < 8) {
      newErrors.newPassword = 'Passwort muss mindestens 8 Zeichen lang sein';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(passwordForm?.newPassword)) {
      newErrors.newPassword = 'Passwort muss Groß-, Kleinbuchstaben und Zahlen enthalten';
    }

    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateEmailForm = () => {
    const newErrors = {};

    if (!emailForm?.newEmail) {
      newErrors.newEmail = 'E-Mail-Adresse ist erforderlich';
    } else if (!/\S+@\S+\.\S+/?.test(emailForm?.newEmail)) {
      newErrors.newEmail = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    if (!emailForm?.password) {
      newErrors.password = 'Passwort zur Bestätigung erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handlePasswordChange = async () => {
    if (!validatePasswordForm()) return;

    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsChangingPassword(false);
      setErrors({});

      // Show success feedback
      alert('Passwort erfolgreich geändert');
    } catch (error) {
      console.error('Password change failed:', error);
      setErrors({ general: 'Fehler beim Ändern des Passworts' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailChange = async () => {
    if (!validateEmailForm()) return;

    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      onUpdate?.({ email: emailForm?.newEmail });
      setIsChangingEmail(false);
      setErrors({});

      // Show success feedback
      alert('E-Mail-Adresse erfolgreich geändert. Bitte bestätigen Sie Ihre neue E-Mail-Adresse.');
    } catch (error) {
      console.error('Email change failed:', error);
      setErrors({ general: 'Fehler beim Ändern der E-Mail-Adresse' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreferenceToggle = (key) => {
    setUserPreferences(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) return;

    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In real app, this would log user out and redirect
      alert('Konto wurde gelöscht');
    } catch (error) {
      console.error('Account deletion failed:', error);
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground">Konto-Einstellungen</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Verwalten Sie Ihre E-Mail-Adresse, Passwort und Präferenzen
        </p>
      </div>

      {/* Email Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-foreground">E-Mail-Adresse</h3>
            <p className="text-sm text-muted-foreground">
              Ihre aktuelle E-Mail-Adresse für Anmeldung und Benachrichtigungen
            </p>
          </div>
          {!isChangingEmail && (
            <Button
              onClick={() => setIsChangingEmail(true)}
              variant="outline"
              size="sm"
              iconName="Edit"
            >
              Ändern
            </Button>
          )}
        </div>

        {!isChangingEmail ? (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Icon name="Mail" size={16} className="text-muted-foreground" />
              <span className="text-foreground">{user?.email}</span>
            </div>
            {user?.emailVerified && (
              <div className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-full">
                <Icon name="CheckCircle" size={12} />
                <span className="text-xs font-medium">Verifiziert</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              label="Neue E-Mail-Adresse"
              type="email"
              value={emailForm?.newEmail}
              onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e?.target?.value }))}
              error={errors?.newEmail}
              required
            />
            <Input
              label="Passwort zur Bestätigung"
              type="password"
              value={emailForm?.password}
              onChange={(e) => setEmailForm(prev => ({ ...prev, password: e?.target?.value }))}
              error={errors?.password}
              required
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  setIsChangingEmail(false);
                  setEmailForm({ newEmail: user?.email, password: '' });
                  setErrors({});
                }}
                variant="outline"
                size="sm"
                disabled={isSaving}
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleEmailChange}
                loading={isSaving}
                size="sm"
              >
                E-Mail ändern
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Password Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-foreground">Passwort</h3>
            <p className="text-sm text-muted-foreground">
              Ändern Sie Ihr Anmelde-Passwort
            </p>
          </div>
          {!isChangingPassword && (
            <Button
              onClick={() => setIsChangingPassword(true)}
              variant="outline"
              size="sm"
              iconName="Key"
            >
              Passwort ändern
            </Button>
          )}
        </div>

        {!isChangingPassword ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="Lock" size={16} />
            <span>••••••••••••</span>
            <span className="text-xs">Letztes Update: vor 2 Monaten</span>
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              label="Aktuelles Passwort"
              type="password"
              value={passwordForm?.currentPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e?.target?.value }))}
              error={errors?.currentPassword}
              required
            />
            <Input
              label="Neues Passwort"
              type="password"
              value={passwordForm?.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e?.target?.value }))}
              error={errors?.newPassword}
              description="Mindestens 8 Zeichen mit Groß-, Kleinbuchstaben und Zahlen"
              required
            />
            <Input
              label="Neues Passwort bestätigen"
              type="password"
              value={passwordForm?.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
              error={errors?.confirmPassword}
              required
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setErrors({});
                }}
                variant="outline"
                size="sm"
                disabled={isSaving}
              >
                Abbrechen
              </Button>
              <Button
                onClick={handlePasswordChange}
                loading={isSaving}
                size="sm"
              >
                Passwort ändern
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Preferences */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Präferenzen</h3>
        
        <div className="space-y-4">
          {preferences?.map((pref) => (
            <div key={pref?.key} className="flex items-start justify-between py-3 border-b border-border last:border-b-0">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-medium text-foreground">{pref?.title}</h4>
                </div>
                <p className="text-sm text-muted-foreground">{pref?.description}</p>
              </div>
              
              <button
                onClick={() => handlePreferenceToggle(pref?.key)}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  userPreferences?.[pref?.key] ? "bg-primary" : "bg-muted"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-background transition-transform",
                    userPreferences?.[pref?.key] ? "translate-x-6" : "translate-x-1"
                  )}
                />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <Button size="sm" iconName="Save">
            Präferenzen speichern
          </Button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-card rounded-lg border border-destructive/20 p-6">
        <div className="flex items-start gap-4">
          <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
            <Icon name="AlertTriangle" size={16} className="text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-destructive mb-2">Konto löschen</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Das Löschen Ihres Kontos ist unwiderruflich. Alle Ihre Daten, AI-Agents und Konfigurationen gehen verloren.
            </p>
            
            {!showDeleteConfirm ? (
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="destructive"
                size="sm"
                iconName="Trash2"
              >
                Konto löschen
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-destructive/10 rounded-lg">
                  <p className="text-sm text-destructive font-medium">
                    Sind Sie sicher, dass Sie Ihr Konto löschen möchten?
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setShowDeleteConfirm(false)}
                    variant="outline"
                    size="sm"
                    disabled={isSaving}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={handleDeleteAccount}
                    variant="destructive"
                    size="sm"
                    loading={isSaving}
                    iconName="Trash2"
                  >
                    Konto endgültig löschen
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;